import { Button } from '@/components/ui/button'
import { DATATYPE, SORT_ORDER } from '@/constants'
import type { Column, Row } from '@tanstack/react-table'
import { ArrowUpDown } from 'lucide-react'

// Helper function for getting sort direction
export const getSortDirection = (sortDirection: false | 'asc' | 'desc') => {
  if (sortDirection === SORT_ORDER.ASC.toLowerCase()) return 'ascending'
  if (sortDirection === SORT_ORDER.DESC.toLowerCase()) return 'descending'
  return 'none'
}

// Helper function for getting next sort direction
export const getNextSortDirection = (sortDirection: false | 'asc' | 'desc') => {
  return sortDirection === SORT_ORDER.ASC.toLowerCase()
    ? 'descending'
    : 'ascending'
}

export const generateColumnHeader = <T,>(
  label: string,
  isSortable: boolean
) => {
  return ({ column }: { column: Column<T> }) => {
    const sortDirection = column.getIsSorted()
    const nextDirection = getNextSortDirection(sortDirection)
    const currentSort = getSortDirection(sortDirection)

    return (
      <Button
        className=" max-w-75  "
        variant="ghost"
        {...(isSortable && {
          onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
          'aria-label': `Sort by ${label} ${nextDirection}`,
          'aria-sort': currentSort,
        })}
      >
        {label}
        {isSortable && (
          <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
        )}
      </Button>
    )
  }
}

export const getSortingFn = <T extends Record<string, unknown>>(
  accessorKey: string,
  dataType: string
) => {
  return (rowA: Row<T>, rowB: Row<T>) => {
    const dataA = rowA.original[accessorKey]
    const dataB = rowB.original[accessorKey]
    switch (dataType) {
      case DATATYPE.STRING:
        return String(dataA ?? '').localeCompare(String(dataB ?? ''))
      case DATATYPE.NUMBER:
        return (Number(dataA) || 0) - (Number(dataB) || 0)
      case DATATYPE.DATE:
        return (
          new Date(String(dataA)).getTime() - new Date(String(dataB)).getTime()
        )
      default:
        return String(dataA ?? '').localeCompare(String(dataB ?? ''))
    }
  }
}
